/**
 * Cleans the free-text note an invitee types before it is sent.
 *
 * Mirrors the server's `InviteeNoteSanitizer`. It is a convenience, not a control: the API re-runs
 * the same cleaning on arrival, because anything enforced only in the browser is enforced nowhere.
 * Keeping the two in step means what the invitee sees in the box is what the host reads.
 *
 * Markup is deliberately left alone. A note saying `x < y` is legitimate and must survive; the
 * defence against injection is escaping where the value is rendered (the HTML mail part escapes,
 * React escapes by default), not mangling it on the way in. What is removed are characters that
 * are never meant in a note and misbehave wherever the text lands: control codes, and the
 * direction overrides and zero-width characters that let text display as something other than what
 * was stored.
 */

// C0 controls (keeping \n and \t), DEL, C1 controls, zero-width and direction-shifting characters.
const STRIPPED = new RegExp(
  "[\\u0000-\\u0008\\u000B\\u000C\\u000E-\\u001F\\u007F-\\u009F" +
    "\\u200B-\\u200F\\u202A-\\u202E\\u2066-\\u2069\\uFEFF]",
  "g",
);

export function cleanNote(raw: string): string {
  return raw.replace(/\r\n?/g, "\n").replace(STRIPPED, "").trim();
}

/** The cleaned note, or undefined when nothing meaningful is left, ready for a request body. */
export function noteForRequest(raw: string): string | undefined {
  return cleanNote(raw) || undefined;
}
